import React, { useEffect, useMemo, useState } from 'react';

import { Stack } from '@mantine/core';
import WorkoutDetail from 'components/Workout/WorkoutDetail';
import type { PreviewMovementExtraRenderer } from 'pages/private/owner/Workouts/Library/components/WorkoutLibrary/WorkoutTemplates/components/ModalWorkoutTemplatePreview/types';
import { buildPreviewDataFromWorkout } from 'pages/private/owner/Workouts/Programming/components/AssignWorkoutModal/utils/templateToWorkoutWrite';
import type { WorkoutRetrieve } from 'types/generated/models/WorkoutRetrieve';
import { getAlphabetLetterByIndex } from 'utils/functions';

import WorkoutSectionSelector, {
  type SectionOption,
} from '../WorkoutSectionSelector/WorkoutSectionSelector';

interface WorkoutDetailViewProps {
  workout: WorkoutRetrieve;
  isCoachView: boolean;
  isFullscreen?: boolean;
  enlarged?: boolean;
  renderMovementExtra?: PreviewMovementExtraRenderer;
}

const WorkoutDetailView = ({
  workout,
  isCoachView,
  isFullscreen = false,
  enlarged = false,
  renderMovementExtra,
}: WorkoutDetailViewProps): JSX.Element => {
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  const sectionOptions = useMemo(
    (): SectionOption[] =>
      [...workout.sections]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((section, index) => ({
          key: section.workoutSectionId,
          label: `Section ${getAlphabetLetterByIndex(index)} - ${section.name}`,
        })),
    [workout.sections],
  );

  useEffect(() => {
    setSelectedSectionId(null);
  }, [workout.workoutId]);

  const sortedSections = [...workout.sections].sort((a, b) => a.orderIndex - b.orderIndex);
  const selectedIndex = selectedSectionId
    ? sortedSections.findIndex(s => s.workoutSectionId === selectedSectionId)
    : 0;
  const isFullView = selectedSectionId === null;
  const filteredSections =
    selectedSectionId && selectedIndex >= 0 ? [sortedSections[selectedIndex]] : sortedSections;

  const previewData = buildPreviewDataFromWorkout({
    ...workout,
    sections: filteredSections,
    athleteNotes: isFullView ? workout.athleteNotes : null,
    coachNotes: isFullView ? workout.coachNotes : null,
  });

  return (
    <Stack spacing='lg'>
      <WorkoutSectionSelector
        sections={sectionOptions}
        selectedKey={selectedSectionId}
        onSelect={setSelectedSectionId}
      />
      <WorkoutDetail
        template={previewData}
        showCoachNotes={isCoachView && !isFullscreen}
        indexOffset={Math.max(selectedIndex, 0)}
        enlarged={enlarged}
        renderMovementExtra={renderMovementExtra}
      />
    </Stack>
  );
};

export default WorkoutDetailView;
